import cx from 'classnames'
import { useSettings, useStyles, useTextArea } from 'neuraum-angus'
import React, { CSSProperties, useEffect, useState } from 'react'

import { get } from '@js/common/api'
import { FetchStatus } from '@js/common/hooks/useStatus'

import { errorLogger } from '@f-fertighaus/utils/errorLogger'

interface LinkPreviewProps {
  message: string
}

interface PreviewData {
  title?: string | null
  description?: string | null
  image?: string | null
  url?: string
  status: FetchStatus
}

const LINK_PREVIEW_ENDPOINT = 'link-preview'

export const MessageWithLinkPreview = ({ message }: LinkPreviewProps) => {
  const [previewData, setPreviewData] = useState<PreviewData[]>([])
  const [displayPreview, setDisplayPreview] = useState(false)
  const [displayedMessage, setDisplayedMessage] = useState('')
  const { toggleTextAreaDisabled } = useTextArea()

  useEffect(() => {
    const fetchData = async () => {
      const manyUrl = [...message.matchAll(/href="([^"]+)"/g)].map(
        match => match[1]
      )

      setPreviewData(
        manyUrl.map(url => ({ url: url.trim(), status: 'fetching' }))
      )

      try {
        const responses = await Promise.allSettled(
          manyUrl.map(url => get(`${LINK_PREVIEW_ENDPOINT}?url=${url}`))
        )

        responses.forEach((response, index) => {
          try {
            const data = response.value.data
            const currentData = previewData[index]
            const dataToUpdate = {
              ...currentData,
              ...data,
              url: manyUrl[index],
              status: 'resolved'
            }

            setPreviewData(prevState =>
              prevState.map((prevStateData, idx) =>
                idx === index
                  ? { ...dataToUpdate, status: 'resolved' }
                  : prevStateData
              )
            )
          } catch (error) {
            errorLogger(error)
            setPreviewData(prevState =>
              prevState.map((prevStateData, idx) =>
                idx === index
                  ? { ...prevStateData, status: 'error' }
                  : prevStateData
              )
            )
          }
        })
      } catch (error) {
        errorLogger(error)
        setPreviewData(prevState =>
          prevState.map(data => ({ ...data, status: 'error' }))
        )
      }
    }

    fetchData()
  }, [message])

  useEffect(() => {
    let index = 0
    const parsedMessage = parseMarkupMessage(message)
    toggleTextAreaDisabled()
    const interval = setInterval(() => {
      setDisplayedMessage(parsedMessage.slice(0, index).join(''))
      index++
      if (index > message.length) {
        clearInterval(interval)
        toggleTextAreaDisabled()
        setDisplayPreview(true)
      }
    }, 10)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <BotMessageContainer>
      <DangerouslySetInnerHTML message={displayedMessage} />
      <div
        className={cx(
          'f-d-flc f-fl-gap-010 f-m-t-020 f-fl-gap-005',
          displayPreview ? 'f-d-flr' : 'f-hidden'
        )}>
        {previewData.map((data, index) => {
          return {
            resolved: <LinkPreview previewData={data} key={index} />,
            error: null,
            fetching: <LinkPreviewSkeleton key={index} />,
            initial: null
          }[data.status]
        })}
      </div>
    </BotMessageContainer>
  )
}

const LinkPreviewContainer = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cx('f-d-flr f-c-ba-bac010 f-fl-gap-010', className)}
      style={{ borderRadius: '4px', padding: '8px' }}>
      {children}
    </div>
  )
}

const LinkPreview = ({ previewData }: { previewData: PreviewData }) => {
  return (
    <a href={previewData.url} className='Chatbot-LinkPreview-link_container'>
      <LinkPreviewContainer>
        <img
          src={previewData.image}
          width={150}
          height={100}
          rel='preload'
          style={{ borderRadius: '4px', width: '40%' }}
          alt='preview'
        />
        <div
          style={{
            gap: '8px',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 3
          }}>
          <div className='f-t-micro f-t-bold' style={{ lineHeight: 1.33 }}>
            {previewData.title}
          </div>
          <div className='f-t-micro Chatbot-LinkPreview-description'>
            {previewData.description}
          </div>
        </div>
      </LinkPreviewContainer>
    </a>
  )
}

const LinkPreviewSkeleton = () => {
  return (
    <LinkPreviewContainer className='Chatbot-LinkPreview-skeleton'>
      <div
        className='f-c-ba-neu100'
        style={{
          width: '40%',
          height: '100px',
          borderRadius: '4px'
        }}></div>
      <div
        style={{
          gap: '24px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 3
        }}>
        <div
          className='f-c-ba-neu100'
          style={{
            height: '20px',
            borderRadius: '4px'
          }}></div>
        <div
          className='f-c-ba-neu100'
          style={{
            height: '15px',
            borderRadius: '4px'
          }}></div>
      </div>
    </LinkPreviewContainer>
  )
}

interface BotMessageContainerProps {
  children: React.ReactNode
}

const BotMessageContainer = ({ children }: BotMessageContainerProps) => {
  const { settings } = useSettings()
  const { styles } = useStyles()

  const botBubbleStyle: CSSProperties = {
    backgroundColor: settings.general?.secondaryColor,
    color: '#fff',
    maxWidth: settings.botBubble?.showAvatar ? '65%' : '70%',
    ...styles.botBubbleStyle
  }

  const offsetStyle = 'rcb-bot-message'

  const botBubbleEntryStyle = settings.botBubble?.animate
    ? 'rcb-bot-message-entry'
    : ''

  return (
    <div
      style={{ ...botBubbleStyle, display: 'inline' }}
      className={`${offsetStyle} ${botBubbleEntryStyle}`}>
      {children}
    </div>
  )
}

const DangerouslySetInnerHTML = (props: { message: string }) => {
  return (
    <span
      dangerouslySetInnerHTML={{ __html: props.message }}
      data-isloading={props.message.length === 0}
    />
  )
}

const parseMarkupMessage = (message: string) => {
  const result: string[] = []
  let currentTag = ''
  let isInTag = false

  for (let i = 0; i < message.length; i++) {
    const char = message[i]

    if (char === '<') {
      // detects start of html tag
      if (!isInTag) {
        isInTag = true
        currentTag = char
      } else {
        result.push(currentTag)
        currentTag = char
      }
    } else if (char === '>') {
      // detects end of html tag
      currentTag += char
      result.push(currentTag)
      currentTag = ''
      isInTag = false
    } else {
      // handles normal character
      if (isInTag) {
        currentTag += char
      } else {
        result.push(char)
      }
    }
  }

  if (currentTag !== '') {
    result.push(currentTag)
  }
  return result
}
